import * as com from "../Common.js"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useDispatch } from "react-redux"
import { debounce } from "lodash"
import {
  Card,
  Switch,
  Form,
  Input,
  DatePicker,
  Row,
  Col,
  Typography,
  InputNumber,
  Button,
  Spin,
  message,
} from "antd"
import { CurrentStatus } from "../Common"
import { PlusOutlined } from "@ant-design/icons"
import "./Appraisal.css"
import dayjs from "dayjs"
import AlertModal from "../Common/components/Modals/AlertModal"
import RuleWarning from "Common/components/RuleWarning"
import { UpdateApplicationAttribute } from "../Store/actions"
import {
  getLosAppraisalV2,
  patchLosAppraisalV2,
  getLosApplicationV2,
} from "../DocumentChecklist/applicationV2Client"

/** Unknown/empty status sorts below ApplicationStarted so OrderAppraisal can still bump. */
const statusRank = (status?: string) => CurrentStatus[status || ""] ?? 0

const { Title, Text } = Typography

const emptyDetail = () => ({
  orderdate: "",
  datecompleted: "",
  appraisalvalue: "",
  appraisalname: "",
  appraisername: "",
  email: "",
  phone: "",
})

const emptyAppraisal = () => ({
  appraisalopened: false,
  appraisaldetails: [] as ReturnType<typeof emptyDetail>[],
})

type AppraisalV2Props = {
  loanId: string
}

export const AppraisalV2 = ({ loanId }: AppraisalV2Props) => {
  const dispatch = useDispatch()
  const [loading, setLoading] = useState(true)
  const [showDeleteAppraisalModal, setShowDeleteAppraisalModal] = useState(false)
  const [deleteAppraisalIndex, setDeleteAppraisalIndex] = useState(0)
  const [appraisalInfo, setAppraisalInfo] = useState(emptyAppraisal())
  const [currentStage, setCurrentStage] = useState("")
  const [currentStatus, setCurrentStatus] = useState("")
  const appraisalRef = useRef(appraisalInfo)
  const pipelineRef = useRef({ currentStage: "", currentStatus: "" })

  useEffect(() => {
    appraisalRef.current = appraisalInfo
  }, [appraisalInfo])

  useEffect(() => {
    pipelineRef.current = { currentStage, currentStatus }
  }, [currentStage, currentStatus])

  const persist = useMemo(
    () =>
      debounce(async (nextInfo, pipeline?: { currentStage?: string; currentStatus?: string }) => {
        if (!loanId) return
        try {
          const payload: any = { appraisalInfo: nextInfo }
          // Only write pipeline when explicitly bumped (ordering appraisal), not on every field edit.
          if (pipeline?.currentStage) payload.currentStage = pipeline.currentStage
          if (pipeline?.currentStatus) payload.currentStatus = pipeline.currentStatus
          await patchLosAppraisalV2(loanId, payload)
        } catch (err: any) {
          console.error(err)
          message.error(err?.message || "Failed to save appraisal")
        }
      }, 600),
    [loanId]
  )

  useEffect(() => () => {
    persist.flush()
    persist.cancel()
  }, [persist])

  useEffect(() => {
    if (!loanId) return undefined
    let cancelled = false
    setLoading(true)
    Promise.all([getLosAppraisalV2(loanId), getLosApplicationV2(loanId)])
      .then(([appraisalBody, appBody]) => {
        if (cancelled) return
        const info = appraisalBody.appraisalInfo || {}
        setAppraisalInfo({
          ...emptyAppraisal(),
          ...info,
          appraisaldetails: info.appraisaldetails || [],
        })
        const app = appBody.Application || {}
        setCurrentStage(app.currentStage || "")
        setCurrentStatus(app.currentStatus || "")
      })
      .catch((err) => {
        if (!cancelled) {
          console.error(err)
          message.error(err?.message || "Failed to load appraisal")
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [loanId])

  const saveInfo = useCallback(
    (nextInfo) => {
      setAppraisalInfo(nextInfo)
      persist(nextInfo)
    },
    [persist]
  )

  const updateField = (value: any, key: string, index?: number) => {
    const current = appraisalRef.current
    let next
    if (key === "appraisaldetails") {
      next = { ...current, appraisaldetails: value }
    } else if (index !== undefined) {
      const details = [...(current.appraisaldetails || [])]
      while (details.length <= index) details.push(emptyDetail())
      details[index] = { ...details[index], [key]: value }
      next = { ...current, appraisaldetails: details }
    } else {
      next = { ...current, [key]: value }
    }
    saveInfo(next)
  }

  const addNewAppraisal = () => {
    const details = [...(appraisalRef.current.appraisaldetails || []), emptyDetail()]
    saveInfo({ ...appraisalRef.current, appraisaldetails: details })
  }

  const onToggleOpened = (checked: boolean) => {
    let pipeline: { currentStage?: string; currentStatus?: string } | undefined
    if (checked && statusRank(pipelineRef.current.currentStatus) < CurrentStatus["OrderAppraisal"]) {
      pipeline = { currentStage: "Processing", currentStatus: "OrderAppraisal" }
      setCurrentStage(pipeline.currentStage!)
      setCurrentStatus(pipeline.currentStatus!)
      // Header StageStatusForm reads Redux; persist still goes to V2 split tables only.
      dispatch(UpdateApplicationAttribute(pipeline.currentStage, "currentStage"))
      dispatch(UpdateApplicationAttribute(pipeline.currentStatus, "currentStatus"))
    }
    let details = [...(appraisalRef.current.appraisaldetails || [])]
    if (checked && details.length === 0) {
      details = [emptyDetail()]
    }
    const next = {
      ...appraisalRef.current,
      appraisalopened: checked,
      appraisaldetails: details,
    }
    setAppraisalInfo(next)
    persist(next, pipeline)
  }

  const displayDeleteAppraisalModal = () => (
    <AlertModal
      isOpen={showDeleteAppraisalModal}
      title=""
      description={
        <div className="text-left">
          <p>Are you sure you want to delete this appraisal?</p>
          <p>This action cannot be undone.</p>
        </div>
      }
      onCancel={() => setShowDeleteAppraisalModal(false)}
      onConfirm={() => {
        const details = [...(appraisalRef.current.appraisaldetails || [])]
        details.splice(deleteAppraisalIndex, 1)
        saveInfo({ ...appraisalRef.current, appraisaldetails: details })
        setShowDeleteAppraisalModal(false)
      }}
    />
  )

  const inputFormatter = (value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")

  if (loading) {
    return (
      <div className="appraisal-container" style={{ padding: 48, textAlign: "center" }}>
        <Spin />
      </div>
    )
  }

  const details = appraisalInfo.appraisaldetails || []

  return (
    <div className="appraisal-container">
      {displayDeleteAppraisalModal()}
      <Card className="appraisal-card">
        <div className="appraisal-header">
          <div className="d-flex align-items-center">
            <div className="appraisal-title">Appraisal Information</div>
            <div className="appraisal-toggle-container">
              <Switch
                className="appraisal-switch"
                checked={!!appraisalInfo.appraisalopened}
                onChange={onToggleOpened}
              />
              <div className="appraisal-toggle-label">
                {appraisalInfo.appraisalopened ? "Appraisal ordered" : "Appraisal not ordered"}
              </div>
            </div>
          </div>
          <Button
            type={"outline" as any}
            icon={<PlusOutlined rev={undefined} style={{ fontWeight: "500" }} />}
            onClick={addNewAppraisal}
            className="add-appraisal-button"
          >
            Add a new appraisal
          </Button>
        </div>

        <Text type="secondary" className="appraisal-description">
          For <b>record-keeping</b> only. Borrowers will only see the updated status, not the detailed information.
        </Text>

        {appraisalInfo.appraisalopened && details.length > 0 && (
          <>
            {details.map((appraisal, index) => (
              <div key={index} className="appraisal-section">
                {details.length > 1 && (
                  <Title level={5} className="d-flex align-items-center">
                    Appraisal {index + 1}{" "}
                    <img
                      src="/images/trash.svg"
                      style={{ width: 22, height: 22, cursor: "pointer", marginLeft: 10 }}
                      onClick={() => {
                        setDeleteAppraisalIndex(index)
                        setShowDeleteAppraisalModal(true)
                      }}
                      alt="delete"
                    />
                  </Title>
                )}
                <Form layout="vertical" className="appraisal-form">
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Order date">
                        <DatePicker
                          style={{ width: "100%" }}
                          placeholder="Choose date"
                          value={appraisal.orderdate ? dayjs(appraisal.orderdate, "MM/DD/YYYY") : null}
                          format="MM/DD/YYYY"
                          onChange={(date) => updateField(date ? date.format("MM/DD/YYYY") : "", "orderdate", index)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Date completed">
                        <DatePicker
                          style={{ width: "100%" }}
                          placeholder="Choose date"
                          format="MM/DD/YYYY"
                          value={appraisal.datecompleted ? dayjs(appraisal.datecompleted, "MM/DD/YYYY") : null}
                          onChange={(date) => updateField(date ? date.format("MM/DD/YYYY") : "", "datecompleted", index)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Appraised value">
                        <InputNumber
                          className="appraisal-money-input"
                          style={{ width: "100%" }}
                          placeholder="0"
                          min={0}
                          formatter={inputFormatter}
                          prefix="$"
                          value={appraisal.appraisalvalue ? parseInt(appraisal.appraisalvalue, 10) : null}
                          onChange={(value) => updateField(value?.toString() || "", "appraisalvalue", index)}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                  <Row gutter={16}>
                    <Col span={12}>
                      <Form.Item label="Appraisal company">
                        <Input
                          placeholder="Enter company name"
                          value={appraisal.appraisalname}
                          onChange={(e) => updateField(e.target.value, "appraisalname", index)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Appraiser name">
                        <Input
                          placeholder="Enter appraiser name"
                          value={appraisal.appraisername}
                          onChange={(e) => updateField(e.target.value, "appraisername", index)}
                        />
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Email">
                        <RuleWarning warning={!/^(?:[^\s@]+@[^\s@]+\.[^\s@]+)?$/.test(appraisal.email || "")}>
                          <Input
                            type="email"
                            placeholder="Enter contact email address"
                            value={appraisal.email}
                            onChange={(e) => updateField(e.target.value, "email", index)}
                          />
                        </RuleWarning>
                      </Form.Item>
                    </Col>
                    <Col span={12}>
                      <Form.Item label="Phone number">
                        <RuleWarning warning={!/^(?:\+1\s?)?\(?\d{3}\)?[\s\-]?\d{3}[\s\-]?\d{4}$|^$/.test(appraisal.phone || "")}>
                          <Input
                            placeholder="Enter contact phone number"
                            value={com.fixPhoneInput(appraisal.phone)}
                            onChange={(e) => updateField(com.fixPhoneInput(e.target.value), "phone", index)}
                          />
                        </RuleWarning>
                      </Form.Item>
                    </Col>
                  </Row>
                </Form>
              </div>
            ))}
          </>
        )}
      </Card>
    </div>
  )
}

export default AppraisalV2
